/* ===================================================================
   Pause infinite animations while the thing they animate is off screen.

   **Why this exists, measured rather than assumed** (`D-141`). Twenty-three
   infinite animations run on the home page and most of them are outside the
   viewport at any moment — the whole hero keeps orbiting, drifting and
   re-stacking `z-index` while the visitor is at the configurator, and the closing
   band's CTA keeps spinning a registered custom property from two screens away.
   An animation that nobody can see costs exactly what one they can see costs:
   animating a registered custom property forces a style recalculation on the host
   every frame, and on its subtree too, because those properties inherit.

   With the stage centred and the hero off screen, six seconds of an *idle* page
   cost 537.6ms of style recalculation. Pausing the off-screen animations took it
   to 338.5ms; pausing the ring animations too — `assets/generator.css` does that
   with `animation-play-state` on the hover states — took it to **zero**. The two
   changes are complementary rather than alternatives, and neither one is visible.

   **It pauses rather than cancels, and that is the whole trick.** A paused
   animation holds its current computed value and resumes from it, so a section
   scrolled away and back continues its motion instead of snapping to the start.
   Measured on a registered custom property: paused at 302.152deg, unchanged 1.5s
   later, continuing from there on resume.

   **Why an attribute and CSS rather than the Web Animations API.** Calling
   `animation.pause()` and `.play()` sets an imperative play state that *overrides*
   `animation-play-state` from the stylesheet. That would silently undo the ring's
   own rule in `assets/generator.css`: the first time this script resumed a ring on
   scroll, that ring would spin permanently, at rest, on every card. Writing an
   attribute and letting CSS resolve it keeps the cascade the single authority —
   off screen wins because it is `!important`, and on screen the ring's own hover
   rule governs again.

   **Reduced motion is not this script's business.** Under `reduce` the animations
   this would manage are either not declared or already stopped by their own media
   queries, so it exits and touches nothing.
   =================================================================== */

(() => {
  if (window.__sdwIdleAnimationsBooted) return;
  window.__sdwIdleAnimationsBooted = true;

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (reduced?.matches) return;
  if (!('IntersectionObserver' in window) || !document.getAnimations) return;

  const ATTR = 'data-sdw-anim-idle';

  /* A generous margin, because the point is to pause what nobody can see and not
     to fight the scroll. A section half a screen away resumes before it arrives,
     so no visitor ever meets a frozen frame mid-animation. */
  const MARGIN = '50% 0px';

  /* Only infinite animations. A one-shot entrance paused off screen would be a
     section that never finishes arriving, and they cost nothing once done. */
  const isInfinite = (animation) => {
    try {
      return animation.effect?.getTiming?.().iterations === Infinity;
    } catch (_) {
      return false;
    }
  };

  const watched = new WeakSet();
  let observer = null;

  const onChange = (entries) => {
    for (const entry of entries) {
      entry.target.toggleAttribute(ATTR, !entry.isIntersecting);
    }
  };

  /* Re-run after anything that can replace a subtree. The theme morphs sections on
     a variant change (`D-105`), so an element observed before the morph is not the
     element on the page after it, and its replacement carries no observation. */
  const scan = () => {
    if (!observer) observer = new IntersectionObserver(onChange, { rootMargin: MARGIN });
    let added = 0;
    for (const animation of document.getAnimations()) {
      if (!isInfinite(animation)) continue;
      const el = animation.effect?.target;
      /* `target` is the originating element even for a `::before`, which is what
         we want — the pseudo cannot be observed and does not need to be. */
      if (!(el instanceof Element) || watched.has(el)) continue;
      watched.add(el);
      observer.observe(el);
      added += 1;
    }
    return added;
  };

  let pending = 0;
  const scanSoon = () => {
    window.clearTimeout(pending);
    pending = window.setTimeout(scan, 120);
  };

  const start = () => {
    scan();
    /* A second pass after the entrance animations have settled: an infinite
       animation declared alongside a one-shot may not have started at first paint,
       and `getAnimations()` only reports what exists now. */
    window.setTimeout(scan, 1200);

    new MutationObserver(scanSoon).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('variant:update', scanSoon);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
